import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Top } from '@/components/Top';
import { splitBubbles } from '@/lib/bubbles';
import { activeLLM, useSettings } from '@/settings/settingsStore';
import { appStore, useAppState } from '@/state/appStore';
import type { Message } from '@/types';

const WEEKDAY = ['日', '一', '二', '三', '四', '五', '六'];

function dayLabel(at: number): string {
  const d = new Date(at);
  const today = new Date();
  const sameYear = d.getFullYear() === today.getFullYear();
  const isToday = sameYear && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
  if (isToday) return '今天';
  return `${sameYear ? '' : `${d.getFullYear()}年`}${d.getMonth() + 1}月${d.getDate()}日 星期${WEEKDAY[d.getDay()]}`;
}

function timeLabel(at: number): string {
  const d = new Date(at);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function sameDay(a: number, b: number): boolean {
  const x = new Date(a);
  const y = new Date(b);
  return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
}

function Row({ m }: { m: Message }) {
  const bubbles = m.error && !m.text ? [] : splitBubbles(m.text);
  return (
    <div className={`row ${m.role === 'user' ? 'me' : 'it'}`}>
      {bubbles.map((b, i) => <div key={i} className="bubble">{b}</div>)}
      {m.error && <div className="bubble err">{m.error}</div>}
      <div className="meta">
        <span>{timeLabel(m.at)}</span>
        {m.model && <span title={m.model}>{m.model.split('/')[1]}</span>}
        <button type="button" onClick={() => { if (window.confirm('删掉这条？它以后就不记得这条说过什么了。')) void appStore.deleteMessage(m.id); }}>删除</button>
      </div>
    </div>
  );
}

export function ChatView() {
  const app = useAppState();
  const settings = useSettings();
  const [draft, setDraft] = useState('');
  const logRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const llm = activeLLM(settings);

  useEffect(() => {
    void appStore.openIfNeeded();
  }, []);

  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [app.messages.length, app.streaming]);

  const send = () => {
    const text = draft;
    setDraft('');
    if (taRef.current) taRef.current.style.height = '';
    void appStore.send(text);
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (draft.trim() && !app.busy) send();
    }
  };

  const grow = (el: HTMLTextAreaElement) => {
    el.style.height = '';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  const streamingBubbles = app.streaming != null ? splitBubbles(app.streaming) : null;

  return (
    <div className="shell">
      <Top sub={llm ? llm.model : '未配置模型'} />
      <div className="log" ref={logRef}>
        {app.messages.length === 0 && !app.streaming && (
          <div className="empty">{llm ? '……' : '先去「设置」里填一个模型的 Key，它才能开口。'}</div>
        )}
        {app.messages.map((m, i) => {
          const prev = app.messages[i - 1];
          const newDay = !prev || !sameDay(prev.at, m.at);
          return (
            <div key={m.id} style={{ display: 'contents' }}>
              {newDay && <div className="day">{dayLabel(m.at)}</div>}
              <Row m={m} />
            </div>
          );
        })}
        {streamingBubbles && (
          <div className="row it">
            {streamingBubbles.map((b, i) => <div key={i} className="bubble">{b}</div>)}
            {streamingBubbles.length === 0 && <div className="bubble typing"><i /><i /><i /></div>}
          </div>
        )}
      </div>
      <div className="composer">
        <textarea
          ref={taRef}
          value={draft}
          placeholder={llm ? '说点什么' : '还没有 Key'}
          rows={1}
          disabled={!llm}
          onChange={(e) => { setDraft(e.target.value); grow(e.target); }}
          onKeyDown={onKey}
        />
        {app.busy ? (
          <button type="button" className="send stop" onClick={() => appStore.stop()}>停</button>
        ) : (
          <button type="button" className="send" disabled={!draft.trim() || !llm} onClick={send}>发送</button>
        )}
      </div>
    </div>
  );
}
